import { FC, useEffect, useState } from 'react'
import { isFulfilled } from '@reduxjs/toolkit'

import { push } from 'connected-react-router'
import CustomButton from '../../components/UI/Buttons/CustomButton'
import Radio from '../../components/UI/Inputs/Radio/Simple'
import CustomPopup from '../../components/UI/Popup'
import { initFacebookSdk, removeFacebookSDKScript } from '../../services/facebook'
import { connectInstagramPage } from '../../services/instagram'

import { useDispatch, useSelector } from '../../store/index'
import {
  TGetFacebookOrInstagramAuthorizedPagesPayload,
  TPage,
  TPostChoosePageFromTheListReturnedByGetFacebookAuthorizedPagesPayload,
  TProjectSocialInstagram
} from '../../store/project/types'
import { addProjectSocialAction } from '../../store/agency/slice'
import {
  getInstagramAuthorizedPagesThunk,
  postChoosePageFromTheListReturnedByGetInstagramAuthorizedPagesThunk
} from '../../store/project/thunks'

import Instagram from '../../assets/img/socials/instagram.svg'

import './ConnectSocialAccounts.scss'
import { generatePath } from 'react-router'
import { AUTH_ROUTE } from '../App/routes'
import { TAddProjectSocialPayload } from '../../store/agency/types'
import { addProjectSocialToCurrentProject } from '../../store/project/slice'
import Preloader from '../../components/Preloader'
import { API_SOCIAL_SERVICE_EXEPTION } from '../../models/errors'

interface ICallBack { }

const FacebookCallBack: FC<ICallBack> = () => {
  const dispatch = useDispatch()

  const { currentProjectId } = useSelector(({ project: { currentProject } }) => ({
    currentProjectId: currentProject.id
  }))

  const [pagesForChoice, setPagesForChoice] = useState<null | TPage[]>(null)
  const [chosenPageId, setChosenPageId] = useState<string>('')
  const [sdkIsLoaded, setSdkIsLoaded] = useState<boolean>(false)

  useEffect(() => {
    const load = async () => {
      await initFacebookSdk()
      setSdkIsLoaded(true)
    }
    load()
    return () => removeFacebookSDKScript()
  }, [])

  useEffect(() => {
    if (currentProjectId && sdkIsLoaded) {
      loginInstagram()
    }
  }, [currentProjectId, sdkIsLoaded])

  const loginInstagram = async () => {
    if (currentProjectId) {
      try {
        const response = await connectInstagramPage()
        console.log('[Connect response] => ', response)
        if (response.authResponse) {
          const { accessToken } = response.authResponse
          const payload: TGetFacebookOrInstagramAuthorizedPagesPayload = {
            project_id: currentProjectId,
            token: accessToken
          }
          const responseGetInstagramAuthorizedPages = await dispatch(
            getInstagramAuthorizedPagesThunk(payload)
          )
          console.log('[Get pages response] => ', responseGetInstagramAuthorizedPages)

          if (isFulfilled(responseGetInstagramAuthorizedPages)) {
            const {
              payload: {
                data: { project_social: projectSocial, pages }
              }
            } = responseGetInstagramAuthorizedPages

            if (projectSocial) {
              const projectSocialPayload: TProjectSocialInstagram = projectSocial
              const payload: TAddProjectSocialPayload = {
                projectId: currentProjectId,
                projectSocial: projectSocialPayload
              }
              if (projectSocial) dispatch(addProjectSocialAction(payload))
              dispatch(addProjectSocialToCurrentProject(payload))
              moveToSocialAccountsPage()
            } else {
              setPagesForChoice(pages)
            }
          } else {
            console.log('[GET PAGES REJECTED]', responseGetInstagramAuthorizedPages)
            if ((responseGetInstagramAuthorizedPages.payload as any)?.error?.code === API_SOCIAL_SERVICE_EXEPTION.EMPTY_RESPONSE) {
              setPagesForChoice([])
            }
          }
        }
      } catch (e) {
        console.log('[ERROR] [Connect response] => ', e)
        console.error(e)
      }
    }
  }

  const moveToSocialAccountsPage = () =>
    dispatch(
      push(
        generatePath(AUTH_ROUTE.SOCIAL_ACCOUNTS, {
          projectId: currentProjectId as number
        })
      )
    )

  const chooseSocialNetworkPage = async () => {
    console.log('[Start connect to project] => id = ', currentProjectId)

    if (currentProjectId) {
      try {
        let payloadInst: TPostChoosePageFromTheListReturnedByGetFacebookAuthorizedPagesPayload = {
          project_id: currentProjectId,
          page_id: chosenPageId
        }
        const responsePostChoosePageFromTheListReturnedByGetInstagramAuthorizedPages =
          await dispatch(
            postChoosePageFromTheListReturnedByGetInstagramAuthorizedPagesThunk(payloadInst)
          )
        console.log('[Connect page to app] => ', { requestPayload: payloadInst, connectResponse: responsePostChoosePageFromTheListReturnedByGetInstagramAuthorizedPages })

        if (isFulfilled(responsePostChoosePageFromTheListReturnedByGetInstagramAuthorizedPages)) {
          const {
            payload: { data: instPage }
          } = responsePostChoosePageFromTheListReturnedByGetInstagramAuthorizedPages

          if (instPage) {
            const payload: TAddProjectSocialPayload = {
              projectId: currentProjectId,
              projectSocial: instPage
            }
            dispatch(addProjectSocialAction(payload))
            dispatch(addProjectSocialToCurrentProject(payload))
          }
          moveToSocialAccountsPage()
        }
      } catch (e) {
        console.log('[ERROR] [Connect page to app] => ', e)
        console.error(e)
      }
    }
  }

  return (
    <div className='page-container'>
      {!pagesForChoice && <Preloader />}
      {!!pagesForChoice && !pagesForChoice.length &&
        <div className='page-choice-empty'>
          <div className='page-choice-empty__title'> No Instagram accounts found. To connect an Instagram account, make sure that: </div>
          <ul className='page-choice-empty__list'>
            <li className='page-choice-empty__list__item'>The Instagram account is a Business account</li>
            <li className='page-choice-empty__list__item'>You’re an admin of the Instagram account</li>
            <li className='page-choice-empty__list__item'>The Instagram account is connected to a Facebook Page</li>
            <li className='page-choice-empty__list__item'>You’re an admin of that Facebook Page</li>
            <li className='page-choice-empty__list__item'>You’re logged into the Facebook account where your an admin of that Page</li>
            <li className='page-choice-empty__list__item'>Attention Nest has permission to post to that Page</li>
          </ul>

          <CustomButton button='primary' onClick={moveToSocialAccountsPage}>Go to the social accounts page</CustomButton>
        </div>
      }
      <CustomPopup
        open={!!pagesForChoice && !!pagesForChoice.length}
        closeOnDocumentClick={false}
        className='page-choice-modal'
      >
        {
          <div className='page-choice-modal-wrapper'>
            Choose your page
            <div className='page-choice-modal_pages'>
              {pagesForChoice &&
                pagesForChoice.map(({ name, id, logo }) => (
                  <div key={id} className='add-post_accounts'>
                    <div className='account'>
                      <div className='account_user-info'>
                        <img
                          className='account_user-info_icon'
                          alt='avatar'
                          src={logo ? logo : Instagram}
                        ></img>
                        <div className='account_user-info_name'>{name}</div>
                      </div>
                      <Radio
                        checked={chosenPageId === `${id}`}
                        handleChange={({ value }: { value: string }) => setChosenPageId(value)}
                        value={id}
                        name='chosePage'
                      />
                    </div>
                  </div>
                ))}
            </div>
            <div className='page-choice-modal_actions jc-between'>
              <CustomButton type='button' button='secondary' onClick={moveToSocialAccountsPage}>
                Back to social accounts page
              </CustomButton>

              <CustomButton
                type='button'
                button='primary'
                disabled={!chosenPageId && !!currentProjectId}
                onClick={chooseSocialNetworkPage}
              >
                Connect
              </CustomButton>
            </div>
          </div>
        }
      </CustomPopup>
    </div>
  )
}

export default FacebookCallBack
